import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  distinctUntilChanged,
  EMPTY,
  merge,
  type Observable,
  of,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { MaybeUrlComponent } from '../../shared/components/maybe-url/maybe-url.component';
import { objectsEqual } from '../../shared/helpers/objects-equal.helper';
import type { PersonalTravelTransaction } from '../../shared/models/api/personal-travel-transaction.model';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { PersonalTravelService } from '../../shared/services/api/personal-travel.service';
import { AuthService } from '../../shared/services/auth.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import { transactionLogTypeTranslation } from '../personal-travel-budget/personal-travel-budget.helpers';

@Component({
  selector: 'opa-personal-travel-details',
  imports: [
    CommonModule,
    OpaCurrencyPipe,
    PorscheDesignSystemModule,
    RouterLink,
    LetDirective,
    MaybeUrlComponent,
  ],
  templateUrl: './personal-travel-details.component.html',
  styleUrl: './personal-travel-details.component.scss',
})
export class PersonalTravelDetailsComponent {
  private readonly authService = inject(AuthService);
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly personalTravelService = inject(PersonalTravelService);
  readonly transactionLogTypeTranslation = transactionLogTypeTranslation;

  readonly isSuperAdmin = this.authService.isSuperAdmin();
  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  personalTravelTransaction$ = this.getPersonalTravelTransaction$();

  reloadEvent(markForCheck = false): void {
    this.personalTravelTransaction$ = this.getPersonalTravelTransaction$();
    if (markForCheck) this.changeDetectorRef.markForCheck();
  }

  private getPersonalTravelTransaction$(): Observable<PersonalTravelTransaction> {
    const routeParams$ = merge(
      of(this.route.snapshot.params),
      this.route.params.pipe(
        startWith(this.route.snapshot.params),
        distinctUntilChanged((a, b) => objectsEqual(a, b)),
        skip(1),
        tap(() => this.reloadEvent(true)),
        switchMap(() => EMPTY),
      ),
    );

    return routeParams$.pipe(
      switchMap((routeParams) => {
        const transactionId = Number(routeParams['id']);
        return this.personalTravelService.getPersonalTravelTransaction(transactionId);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
